import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { Select, SelectOption } from "../../Select";
import { patientsService, Patient } from "../../../services/patientsService";
import {
  ModalContainer,
  ModalTitle,
  ModalSubtitle,
  SelectContainer,
  ButtonsContainer,
  CancelButton,
  AssignButton,
} from "./AssignPatientModal.styled";

const PATIENTS_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 300;

interface AssignPatientModalProps {
  onAssign: (patientIds: number[]) => Promise<void>;
  onCancel: () => void;
  providerId: number;
}

const AssignPatientModalContent: React.FC<AssignPatientModalProps> = ({
  onAssign,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [total, setTotal] = useState(0);
  const [start, setStart] = useState(0);
  const [selectedPatientIds, setSelectedPatientIds] = useState<number[]>([]);
  // Preserves Patient data for selected items so chips survive search/page changes
  const [selectedPatientsMap, setSelectedPatientsMap] = useState<
    Record<number, Patient>
  >({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const currentSearchRef = useRef("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchInitiatedRef = useRef(false);

  const fetchPatients = useCallback(
    async (search: string, startOffset: number) => {
      try {
        if (startOffset === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const result = await patientsService.getPatients({
          hasProvider: false,
          limit: PATIENTS_LIMIT,
          start: startOffset,
          search: search || undefined,
        });

        setPatients((prev) =>
          startOffset === 0 ? result.data : [...prev, ...result.data],
        );
        setTotal(result.total);
        setStart(startOffset + result.data.length);
      } catch (error) {
        console.error("Error fetching unassigned patients:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (fetchInitiatedRef.current) return;
    fetchInitiatedRef.current = true;
    fetchPatients("", 0);
  }, [fetchPatients]);

  const handleSearchChange = useCallback(
    (query: string) => {
      currentSearchRef.current = query;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchPatients(query, 0);
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchPatients],
  );

  const handleLoadMore = useCallback(() => {
    if (start >= total) return;
    fetchPatients(currentSearchRef.current, start);
  }, [fetchPatients, start, total]);

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const patientOptions: SelectOption[] = (() => {
    const currentIds = new Set(patients.map((p) => p.id));
    const preserved = Object.values(selectedPatientsMap).filter(
      (p) => !currentIds.has(p.id),
    );
    return [...patients, ...preserved].map((patient) => ({
      value: patient.id,
      label: `${patient.firstName} ${patient.lastName}`.trim(),
      secondaryLabel: formatDate(patient.dateOfBirth),
    }));
  })();

  const handleSelectChange = (value: string | number | (string | number)[]) => {
    const ids = value as number[];
    setSelectedPatientIds(ids);
    setSelectedPatientsMap((prev) => {
      const next = { ...prev };
      // Add newly selected patients to the map
      ids.forEach((id) => {
        if (!next[id]) {
          const patient = patients.find((p) => p.id === id);
          if (patient) next[id] = patient;
        }
      });
      // Remove deselected patients from the map
      Object.keys(next).forEach((key) => {
        if (!ids.includes(Number(key))) delete next[Number(key)];
      });
      return next;
    });
  };

  const handleAssign = async () => {
    if (selectedPatientIds.length === 0) return;

    try {
      setAssigning(true);
      await onAssign(selectedPatientIds);
    } catch (error) {
      console.error("Error assigning patients:", error);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{t("userProfile.assignPatientModal.title")}</ModalTitle>
      <ModalSubtitle>
        {t("userProfile.assignPatientModal.subtitle")}
      </ModalSubtitle>
      <SelectContainer>
        <Select
          name="patients"
          label={t("userProfile.assignPatientModal.selectPatients")}
          placeholder={t("userProfile.assignPatientModal.selectPatients")}
          value={selectedPatientIds}
          onChange={handleSelectChange}
          options={patientOptions}
          multiple
          showChips
          searchable
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={start < total ? handleLoadMore : undefined}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t(
            "userProfile.assignPatientModal.searchPlaceholder",
          )}
        />
      </SelectContainer>
      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onCancel}
          disabled={assigning}
        >
          {t("userProfile.assignPatientModal.cancel")}
        </CancelButton>
        <AssignButton
          variant="contained"
          onClick={handleAssign}
          disabled={assigning || selectedPatientIds.length === 0}
        >
          {assigning ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("userProfile.assignPatientModal.assign")
          )}
        </AssignButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

export default AssignPatientModalContent;
